import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AttachFileIcon,
  Box,
  ClaimscoreLogoLarge,
  ClaimscoreLogoSmall,
  ClipboardIcon,
  DashboardIcon,
  Flags,
  KeyOutlinedIcon,
  MenuOpenIcon,
  NavItem,
  SettingsIcon,
  Spacing,
  Stack,
  styled,
  SupervisorAccountIcon,
  useBreakpoints,
  useFlag,
  useTheme,
} from 'ui';
import { NavItemProps } from 'ui/components/NavItem/NavItem';
import { RoleLevel } from '@context/UserOrgCase.types';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import { getOrgAndCaseId } from '@utils/getOrgAndCaseId';
import { PATHS } from '@utils/paths';
import { UserRole } from 'services/ClaimscoreApiService/shared/types';
import { SIDENAVBAR_CONTENT_WRAPPER } from './SideNavbar.testIds';

const ListBox = styled(Box)`
  margin-top: ${({ theme }) => theme.spacing(Spacing.Large)};
`;
const LogoBox = styled(Box)`
  padding-left: ${({ theme }) => theme.spacing(Spacing.Small)};
`;
const SidebarBox = styled(Box)`
  display: flex;
  gap: ${({ theme }) => theme.spacing(Spacing.Medium)};
  margin-top: ${({ theme }) => theme.spacing(Spacing.Medium)};
`;

const StyledMenuOpenIcon = styled(MenuOpenIcon)`
  cursor: pointer;
`;

const DASHBOARD: NavItemProps = {
  label: 'Dashboard',
  icon: <DashboardIcon />,
  to: `/dashboard`,
};

const PERMISSIONS: NavItemProps = {
  label: 'Permissions',
  icon: <SupervisorAccountIcon />,
  to: `/permissions/organization`,
  activeUrl: `/permissions/`,
};

const REPORTS_ITEM: NavItemProps = {
  label: 'Reports',
  icon: <ClipboardIcon />,
  to: PATHS.PRIVATE_BASE.scheduledReports,
};

const CASE_SETTINGS: NavItemProps = {
  label: 'Case Settings',
  icon: <SettingsIcon />,
  to: `${PATHS.PRIVATE_BASE.caseSettings}/basic-information`,
};

const CLAIM_FILES: NavItemProps = {
  label: 'Claim Files',
  icon: <AttachFileIcon />,
  to: `${PATHS.PRIVATE_BASE.claimFiles}`,
};

const DEVELOPER_TOOLS: NavItemProps = {
  label: 'Developer Tools',
  icon: <KeyOutlinedIcon />,
  to: '/developer-tools/api-keys',
};

export interface MainSideNavbarContentProps {
  readonly open?: boolean;
  readonly onClose?: () => void;
}

export function MainSideNavbarContent({
  open,
  onClose,
}: MainSideNavbarContentProps) {
  const theme = useTheme();
  const { isMdUp } = useBreakpoints();
  const { getUserRole, isCurrentCaseAdmin, isCurrentOrgAdmin } =
    useUserOrgCase();
  const { search } = useLocation();

  const isScheduledReportsEnabled = useFlag(Flags.scheduleReports);
  const isCaseSettingsEnabled = useFlag(Flags.caseSettings);
  const isUploadClaimFilesEnabled = useFlag(Flags.uploadClaimsFile);
  const isDeveloperToolsEnabled = useFlag(Flags.developerTools);

  const isDeveloper =
    getUserRole(RoleLevel.organization) === UserRole.Developer;

  const getSideNavbarBoxContent = useMemo(() => {
    if (open) {
      return (
        <LogoBox>
          <ClaimscoreLogoLarge
            aria-label="ClaimScore Logo"
            color={theme.palette.paper[200]}
          />
          {!isMdUp && (
            <StyledMenuOpenIcon aria-label="menu-open-icon" onClick={onClose} />
          )}
        </LogoBox>
      );
    }
    return (
      <LogoBox>
        <ClaimscoreLogoSmall
          aria-label="ClaimScore Logo"
          color={theme.palette.paper[200]}
        />
      </LogoBox>
    );
  }, [open, theme.palette.paper, isMdUp, onClose]);

  const getSideNavbarItems = useMemo(() => {
    const sideNavbarItems = [DASHBOARD];

    if (isCurrentOrgAdmin || isCurrentCaseAdmin) {
      sideNavbarItems.push(PERMISSIONS);
    }

    if (isScheduledReportsEnabled && isCurrentCaseAdmin) {
      sideNavbarItems.push(REPORTS_ITEM);
    }
    if (isCaseSettingsEnabled && isCurrentCaseAdmin) {
      sideNavbarItems.push(CASE_SETTINGS);
    }

    if (isUploadClaimFilesEnabled && isCurrentCaseAdmin) {
      sideNavbarItems.push(CLAIM_FILES);
    }

    if (isDeveloperToolsEnabled && (isCurrentOrgAdmin || isDeveloper)) {
      sideNavbarItems.push(DEVELOPER_TOOLS);
    }

    return sideNavbarItems.map(({ label, icon, to, activeUrl }) => (
      <NavItem
        activeUrl={activeUrl}
        icon={icon}
        key={label}
        kind="dark"
        label={label}
        to={`${to}${getOrgAndCaseId(search)}`}
      />
    ));
  }, [
    isCaseSettingsEnabled,
    isCurrentCaseAdmin,
    isCurrentOrgAdmin,
    isDeveloper,
    isDeveloperToolsEnabled,
    isScheduledReportsEnabled,
    isUploadClaimFilesEnabled,
    search,
  ]);

  return (
    <Stack data-testid={SIDENAVBAR_CONTENT_WRAPPER}>
      <SidebarBox>{getSideNavbarBoxContent}</SidebarBox>
      <ListBox>{getSideNavbarItems}</ListBox>
    </Stack>
  );
}
